import React, { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { resolveQRUrl, getRestaurantMenu } from '../api/client';
import { getTemplateComponent } from '../templates/TemplateRegistry';
import { normalizeMenuData } from '../utils/normalizeMenuData';

const MenuRenderer = () => {
    const { shortcode } = useParams();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [templateName, setTemplateName] = useState(null);
    const [menuData, setMenuData] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);

                // 1. Try to get templateName from query string
                const params = new URLSearchParams(location.search);
                let qTemplate = params.get('templateName');

                // 2. If no templateName provided, fall back to resolving via API
                if (!qTemplate) {
                    const resolved = await resolveQRUrl(shortcode);
                    qTemplate = resolved && resolved.templateName;
                }
                setTemplateName(qTemplate);

                // 3. Fetch Menu Data using shortcode
                const data = await getRestaurantMenu(shortcode);
                setMenuData(data);
            } catch (err) {
                console.error('Failed to load menu preview', err);
                setError('Unable to load menu.');
            } finally {
                setLoading(false);
            }
        };

        if (shortcode) {
            fetchData();
        }
    }, [shortcode, location.search]);

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', color: '#666' }}>
                Loading preview...
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ padding: '20px', textAlign: 'center', color: '#e74c3c', marginTop: '50px' }}>
                <h2>Oops!</h2>
                <p>{error}</p>
            </div>
        );
    }

    const TemplateComponent = getTemplateComponent(templateName);

    // Normalize unexpected API response where a single `restaurant` object
    // may be returned instead of the expected `{ restaurant, menus }` shape.
    let normalizedData = menuData;
    if (normalizedData && !normalizedData.menus && (normalizedData.id || normalizedData.name)) {
        normalizedData = { restaurant: normalizedData, menus: [] };
        // eslint-disable-next-line no-console
        console.warn('MenuRenderer: normalizing menuData to { restaurant, menus } shape', menuData);
    }

    // Normalize nested structures (tags as objects -> strings, variants shape, etc.)
    if (normalizedData) {
        try {
            normalizedData = normalizeMenuData(normalizedData);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.warn('MenuRenderer: failed to normalize menuData', e);
        }
    }

    return <TemplateComponent data={normalizedData} />;
};

export default MenuRenderer;
